import { useEffect, useState, useContext, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Dialog } from '@headlessui/react';
import { X, Sparkles } from 'lucide-react';
import { Slot, SlotFilters, BoothMatchResult, VendorBoothPreferences } from '../types';
import { slotApi, boothPreferencesApi } from '../services/api';
import { VendorContext } from '../contexts/VendorContext';
import BoothMatchIndicator from './BoothMatchIndicator';
import { formatCurrency } from '../utils/currency'; // Issue #905: Decimal.js utilities
import {
  getBoothViewPreference,
  saveBoothViewPreference,
  getBoothMatchScoreThreshold,
  saveBoothMatchScoreThreshold,
  DEFAULT_BOOTH_MATCH_SCORE_THRESHOLD,
  getVendorProfilePreference,
  saveVendorProfilePreference
} from '../utils/preferences'; // Issue #789: Booth view persistence, Issue #788: Match score filter, Issue #787: Vendor profile selector
import { useCurrentUser } from '../hooks/useCurrentUser'; // Issue #1149: Centralized user access

interface SlotSelectionModalProps {
  fairId: number;
  fairName: string;
  isOpen: boolean;
  onClose: () => void;
}

export function SlotSelectionModal({ fairId, fairName, isOpen, onClose }: SlotSelectionModalProps) {
  const navigate = useNavigate();
  const vendorContext = useContext(VendorContext);
  const userVendors = useMemo(() => vendorContext?.vendors || [], [vendorContext?.vendors]);
  const { id: currentUserId, isAuthenticated } = useCurrentUser(); // Issue #1149: Use centralized user hook
  const [slots, setSlots] = useState<Slot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<SlotFilters>({
    is_available: true,
    slot_type: [],
    has_power: undefined,
    min_price: undefined,
    max_price: undefined,
  });
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;
  const [viewMode, setViewMode] = useState<'all' | 'recommended'>('all');
  const [recommendedBooths, setRecommendedBooths] = useState<BoothMatchResult[]>([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [recommendationsError, setRecommendationsError] = useState<string | null>(null);
  const [preferenceLoaded, setPreferenceLoaded] = useState(false); // Issue #789: Track if preference was loaded
  const preferenceAppliedRef = useRef(false); // Issue #789: Prevent re-applying preference on recommendations change
  const [matchScoreThreshold, setMatchScoreThreshold] = useState<number>(DEFAULT_BOOTH_MATCH_SCORE_THRESHOLD); // Issue #788: Match score filter threshold
  const [selectedVendorId, setSelectedVendorId] = useState<number | null>(null); // Issue #787: Selected vendor profile for recommendations
  const [profileSwitchLoading, setProfileSwitchLoading] = useState(false); // Issue #1166: Loading state for vendor profile switching
  const [vendorPreferences, setVendorPreferences] = useState<VendorBoothPreferences | null>(null); // Issue #1167: Vendor booth preferences for context-aware empty states
  const [preferencesLoading, setPreferencesLoading] = useState(false); // Issue #1167: Loading state for preferences fetch

  // Initialize selected vendor ID when modal opens (Issue #787, Issue #1149)
  useEffect(() => {
    if (!isOpen || userVendors.length === 0) {
      setSelectedVendorId(null);
      return;
    }

    // Use currentUserId from useCurrentUser hook (Issue #1149)
    if (!isAuthenticated || currentUserId === null) {
      // Default to first vendor if no user ID
      setSelectedVendorId(userVendors[0].id);
      return;
    }

    // Try to load saved vendor profile preference
    const savedVendorId = getVendorProfilePreference(currentUserId);

    // Verify that the saved vendor ID is still in the user's vendor list
    const vendorExists = savedVendorId !== null && userVendors.some(v => v.id === savedVendorId);

    if (vendorExists) {
      setSelectedVendorId(savedVendorId);
    } else {
      // Fall back to first vendor if saved preference is invalid
      setSelectedVendorId(userVendors[0].id);
    }
  }, [isOpen, userVendors, isAuthenticated, currentUserId]);

  // Fetch vendor booth preferences (Issue #1167)
  useEffect(() => {
    if (!isOpen || selectedVendorId === null) return;

    const fetchPreferences = async () => {
      setPreferencesLoading(true);
      try {
        const preferences = await boothPreferencesApi.getPreferences(selectedVendorId);
        setVendorPreferences(preferences);
      } catch (err: unknown) {
        console.error('Failed to load vendor preferences:', err);
        // If preferences don't exist (404), treat as no preferences configured
        setVendorPreferences(null);
      } finally {
        setPreferencesLoading(false);
      }
    };

    fetchPreferences();
  }, [isOpen, selectedVendorId]);

  // Fetch recommended booths (Issue #732, Issue #787, Issue #1166)
  useEffect(() => {
    if (!isOpen || userVendors.length === 0 || selectedVendorId === null) return;

    const fetchRecommendations = async () => {
      setRecommendationsLoading(true);
      setRecommendationsError(null);
      try {
        // Use selected vendor profile for personalized recommendations (Issue #787)
        const results = await boothPreferencesApi.matchPreferences(fairId, {
          vendor_id: selectedVendorId,
        });
        setRecommendedBooths(results);
      } catch (err: unknown) {
        console.error('Failed to load recommendations:', err);
        setRecommendationsError('Unable to load recommendations.');
      } finally {
        setRecommendationsLoading(false);
        setProfileSwitchLoading(false); // Issue #1166: Clear profile switch loading state
      }
    };

    fetchRecommendations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fairId, isOpen, selectedVendorId]);

  // Load saved booth view preference when modal opens (Issue #789, Issue #1149)
  // Load saved match score threshold when modal opens (Issue #788)
  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setPreferenceLoaded(false);
      setViewMode('all'); // Reset to default view mode
      preferenceAppliedRef.current = false; // Reset ref for next open
      setMatchScoreThreshold(DEFAULT_BOOTH_MATCH_SCORE_THRESHOLD); // Reset threshold
      setProfileSwitchLoading(false); // Issue #1166: Reset profile switch loading state
      return;
    }

    // Only load preference once when modal opens
    if (preferenceLoaded) return;

    // Use currentUserId from useCurrentUser hook (Issue #1149)
    if (!isAuthenticated || currentUserId === null) {
      setPreferenceLoaded(true);
      preferenceAppliedRef.current = true;
      return;
    }

    // Load saved preferences
    const savedPreference = getBoothViewPreference(currentUserId);
    const savedThreshold = getBoothMatchScoreThreshold(currentUserId); // Issue #788

    // Load match score threshold (Issue #788)
    if (savedThreshold !== null) {
      setMatchScoreThreshold(savedThreshold);
    }

    // Apply preference based on availability
    if (savedPreference === 'all') {
      setViewMode('all');
      setPreferenceLoaded(true);
      preferenceAppliedRef.current = true;
    } else if (savedPreference === 'recommended') {
      // Wait for recommendations to load before applying
      // Don't set preferenceLoaded yet - will be set in next effect
      return;
    } else {
      // No saved preference or invalid preference
      setPreferenceLoaded(true);
      preferenceAppliedRef.current = true;
    }
  }, [isOpen, preferenceLoaded, isAuthenticated, currentUserId]);

  // Apply 'recommended' preference after recommendations are loaded (Issue #789, Issue #1149)
  useEffect(() => {
    // Don't run if modal is closed, preference already applied, or already loaded
    if (!isOpen || preferenceAppliedRef.current || preferenceLoaded) return;

    // Wait for recommendations to finish loading
    if (recommendationsLoading) return;

    // At this point, recommendations have finished loading (success or error)
    // We need to apply the preference if it exists

    // Use currentUserId from useCurrentUser hook (Issue #1149)
    if (!isAuthenticated || currentUserId === null) {
      setPreferenceLoaded(true);
      preferenceAppliedRef.current = true;
      return;
    }

    const savedPreference = getBoothViewPreference(currentUserId);

    // Only apply 'recommended' preference if recommendations are available
    if (savedPreference === 'recommended' && recommendedBooths.length > 0) {
      setViewMode('recommended');
    }

    // Always set both flags to true once recommendations are done loading
    // This prevents infinite loops even if recommendations failed to load
    setPreferenceLoaded(true);
    preferenceAppliedRef.current = true;
  }, [isOpen, preferenceLoaded, recommendationsLoading, recommendedBooths.length, isAuthenticated, currentUserId]);

  // Fetch available slots
  useEffect(() => {
    if (!isOpen || viewMode !== 'all') return;

    const fetchSlots = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await slotApi.searchSlots(fairId, { ...filters, page, page_size: pageSize });
        setSlots(response.items);
        setTotalCount(response.total);
      } catch (err: unknown) {
        console.error('Failed to load slots:', err);
        setError('Failed to load available booths. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSlots();
  }, [fairId, filters, page, isOpen, viewMode]);

  // Filter recommended booths by match score threshold (Issue #788)
  const filteredRecommendedBooths = useMemo(() => {
    return recommendedBooths.filter(match => match.match_score >= matchScoreThreshold);
  }, [recommendedBooths, matchScoreThreshold]);

  // Handle view mode change and save preference (Issue #789, Issue #1149)
  const handleViewModeChange = (newViewMode: 'all' | 'recommended') => {
    setViewMode(newViewMode);

    // Save preference using currentUserId from useCurrentUser hook (Issue #1149)
    if (isAuthenticated && currentUserId !== null) {
      saveBoothViewPreference(currentUserId, newViewMode);
    }
  };

  // Handle match score threshold change and save preference (Issue #788, Issue #1149)
  const handleMatchScoreThresholdChange = (newThreshold: number) => {
    setMatchScoreThreshold(newThreshold);

    // Save preference using currentUserId from useCurrentUser hook (Issue #1149)
    if (isAuthenticated && currentUserId !== null) {
      saveBoothMatchScoreThreshold(currentUserId, newThreshold);
    }
  };

  // Handle vendor profile change and save preference (Issue #787, Issue #1149, Issue #1166)
  const handleVendorProfileChange = (newVendorId: number) => {
    setProfileSwitchLoading(true); // Issue #1166: Show loading indicator
    setSelectedVendorId(newVendorId);

    // Save preference using currentUserId from useCurrentUser hook (Issue #1149)
    if (isAuthenticated && currentUserId !== null) {
      saveVendorProfilePreference(currentUserId, newVendorId);
    }
  };

  const handleReserveSlot = (slotId: number) => {
    navigate(`/slots/${slotId}/reserve`);
    onClose();
  };

  const handleShowMore = () => {
    setPage(prev => prev + 1);
  };

  // Helper function to check if vendor has any booth preferences configured (Issue #1167)
  const hasBoothPreferences = useMemo(() => {
    if (!vendorPreferences) return false;

    // Check if any boolean preference is enabled
    const hasBooleanPrefs = vendorPreferences.prefers_corner ||
      vendorPreferences.prefers_high_traffic ||
      vendorPreferences.prefers_near_entrance ||
      vendorPreferences.requires_power ||
      vendorPreferences.prefers_away_from_competitors;

    // Check if any optional fields are set
    const hasOptionalPrefs = (vendorPreferences.preferred_neighbors && vendorPreferences.preferred_neighbors.length > 0) ||
      vendorPreferences.budget_min ||
      vendorPreferences.budget_max ||
      vendorPreferences.min_dimensions ||
      (vendorPreferences.notes && vendorPreferences.notes.trim().length > 0);

    return hasBooleanPrefs || hasOptionalPrefs;
  }, [vendorPreferences]);

  // Get selected vendor business name for display (Issue #1167)
  const selectedVendorName = useMemo(() => {
    if (!selectedVendorId) return '';
    const vendor = userVendors.find(v => v.id === selectedVendorId);
    return vendor?.business_name || '';
  }, [selectedVendorId, userVendors]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-3xl w-full bg-white rounded-lg shadow-xl max-h-[85vh] overflow-y-auto">
          <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <Dialog.Title className="text-2xl font-bold">
                Reserve a Booth at {fairName}
              </Dialog.Title>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition"
                aria-label="Close modal"
              >
                <X size={24} />
              </button>
            </div>

            <Dialog.Description className="text-gray-600 mb-4">
              Browse and filter available vendor booths. Click "Reserve This Booth" to start the booking process.
            </Dialog.Description>

            {/* View Mode Toggle (Issue #732, Issue #789) */}
            {userVendors.length > 0 && recommendedBooths.length > 0 && !recommendationsLoading && (
              <div className="bg-gray-50 p-4 rounded-lg mb-4 space-y-4">
                {/* Vendor Profile Selector (Issue #787, Issue #1166) - Only show if user has multiple vendor profiles */}
                {userVendors.length > 1 && selectedVendorId !== null && (
                  <div className="space-y-2">
                    <label
                      htmlFor="vendor-profile-selector"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Show recommendations for:
                    </label>
                    <div className="relative">
                      <select
                        id="vendor-profile-selector"
                        value={selectedVendorId}
                        onChange={(e) => handleVendorProfileChange(parseInt(e.target.value, 10))}
                        disabled={profileSwitchLoading}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-white focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        aria-label="Select vendor profile for booth recommendations"
                        data-testid="vendor-profile-selector"
                      >
                        {userVendors.map((vendor) => (
                          <option key={vendor.id} value={vendor.id}>
                            {vendor.business_name}
                          </option>
                        ))}
                      </select>
                      {profileSwitchLoading && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" role="status" aria-label="Loading recommendations">
                          <div className="animate-spin h-5 w-5 border-2 border-purple-500 border-t-transparent rounded-full" aria-hidden="true"></div>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500">
                      Recommendations are personalized based on each vendor profile's preferences.
                    </p>
                  </div>
                )}

                <div
                  className="flex items-center gap-3"
                  role="group"
                  aria-label="Booth view mode selection"
                >
                  <button
                    onClick={() => handleViewModeChange('all')}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition ${
                      viewMode === 'all'
                        ? 'bg-primary text-white shadow-sm'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                    aria-pressed={viewMode === 'all'}
                    aria-label="View all available booths"
                  >
                    All Booths
                  </button>
                  <button
                    onClick={() => handleViewModeChange('recommended')}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
                      viewMode === 'recommended'
                        ? 'bg-purple-600 text-white shadow-sm'
                        : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                    }`}
                    aria-pressed={viewMode === 'recommended'}
                    aria-label={`View ${filteredRecommendedBooths.length} recommended booths based on your preferences${matchScoreThreshold > 0 ? ` matching ${matchScoreThreshold}% or higher` : ''}`}
                  >
                    <Sparkles className="w-4 h-4" aria-hidden="true" />
                    Recommended ({filteredRecommendedBooths.length}{matchScoreThreshold > 0 ? ` matching ${matchScoreThreshold}%+` : ''})
                  </button>
                </div>

                {/* Match Score Filter (Issue #788) - Only show in recommended view */}
                {viewMode === 'recommended' && (
                  <div className="space-y-2" data-testid="match-score-filter">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <label htmlFor="match-score-slider" className="text-sm font-medium text-gray-700">
                        Minimum Match Score: {matchScoreThreshold}%
                      </label>
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => handleMatchScoreThresholdChange(75)}
                          className={`text-xs px-2 py-1 rounded transition ${
                            matchScoreThreshold === 75
                              ? 'bg-purple-600 text-white'
                              : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                          }`}
                          aria-label="Set threshold to 75% (Best Matches)"
                          data-testid="preset-best-matches"
                        >
                          Best (75%+)
                        </button>
                        <button
                          onClick={() => handleMatchScoreThresholdChange(50)}
                          className={`text-xs px-2 py-1 rounded transition ${
                            matchScoreThreshold === 50
                              ? 'bg-purple-600 text-white'
                              : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                          }`}
                          aria-label="Set threshold to 50% (Good Matches)"
                          data-testid="preset-good-matches"
                        >
                          Good (50%+)
                        </button>
                        <button
                          onClick={() => handleMatchScoreThresholdChange(0)}
                          className={`text-xs px-2 py-1 rounded transition ${
                            matchScoreThreshold === 0
                              ? 'bg-purple-600 text-white'
                              : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                          }`}
                          aria-label="Set threshold to 0% (All Matches)"
                          data-testid="preset-all-matches"
                        >
                          All
                        </button>
                      </div>
                    </div>
                    <input
                      id="match-score-slider"
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      value={matchScoreThreshold}
                      onChange={(e) => handleMatchScoreThresholdChange(parseInt(e.target.value, 10))}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
                      aria-label="Match score threshold slider"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={matchScoreThreshold}
                      aria-valuetext={`${matchScoreThreshold}% minimum match score`}
                      aria-describedby="slider-help"
                      data-testid="match-score-slider"
                    />
                    <p id="slider-help" className="text-xs text-gray-500 sr-only">
                      Use arrow keys to adjust match score threshold. Left/Down decrease, Right/Up increase. Home sets to 0%, End sets to 100%.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Filter Panel - Only show for 'all' view */}
            {viewMode === 'all' && (
            <div className="bg-gray-50 p-4 rounded-lg mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label htmlFor="slot-type-filter" className="block text-sm font-medium mb-1">
                  Booth Type
                </label>
                <select
                  id="slot-type-filter"
                  value={filters.slot_type && filters.slot_type.length > 0 ? filters.slot_type[0] : ''}
                  onChange={e => setFilters(prev => ({
                    ...prev,
                    slot_type: e.target.value ? [e.target.value] : []
                  }))}
                  className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">All Types</option>
                  <option value="common">Common</option>
                  <option value="preferred">Preferred</option>
                  <option value="premium">Premium</option>
                </select>
              </div>
              <div>
                <label htmlFor="power-filter" className="block text-sm font-medium mb-1">
                  Power
                </label>
                <select
                  id="power-filter"
                  value={filters.has_power === undefined ? '' : filters.has_power.toString()}
                  onChange={e => setFilters(prev => ({
                    ...prev,
                    has_power: e.target.value === '' ? undefined : e.target.value === 'true'
                  }))}
                  className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
                >
                  <option value="">Any</option>
                  <option value="true">Power Available</option>
                  <option value="false">No Power</option>
                </select>
              </div>
              <div>
                <label htmlFor="max-price-filter" className="block text-sm font-medium mb-1">
                  Max Price
                </label>
                <input
                  id="max-price-filter"
                  type="number"
                  placeholder="Any"
                  value={filters.max_price || ''}
                  onChange={e => setFilters(prev => ({
                    ...prev,
                    max_price: e.target.value ? parseFloat(e.target.value) : undefined
                  }))}
                  className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-primary focus:border-primary"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
            )}

            {/* Slot List */}
            <div className="space-y-3 mb-4" role="region" aria-label={viewMode === 'recommended' ? 'Recommended booths' : 'All available booths'}>
              {/* Recommended View */}
              {viewMode === 'recommended' ? (
                recommendationsLoading || profileSwitchLoading ? (
                  <div className="text-center py-8 text-gray-500" role="status" aria-live="polite">
                    <div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-2" aria-hidden="true"></div>
                    <span>
                      {profileSwitchLoading && selectedVendorId !== null
                        ? `Loading recommendations for ${userVendors.find(v => v.id === selectedVendorId)?.business_name || 'selected vendor'}...`
                        : 'Loading recommendations...'}
                    </span>
                  </div>
                ) : recommendationsError ? (
                  <div className="text-center py-8">
                    <p className="text-red-600 mb-3">{recommendationsError}</p>
                    <button
                      onClick={() => handleViewModeChange('all')}
                      className="text-secondary hover:text-primary-dark font-medium"
                    >
                      View All Booths
                    </button>
                  </div>
                ) : filteredRecommendedBooths.length === 0 ? (
                  <div className="text-center py-8 text-gray-500" data-testid="empty-state">
                    <Sparkles className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    {recommendedBooths.length === 0 ? (
                      preferencesLoading ? (
                        // Loading preferences (Issue #1167)
                        <p className="mb-3" data-testid="preferences-loading-message">Checking booth preferences...</p>
                      ) : !hasBoothPreferences ? (
                        // Scenario 1: No Booth Preferences Configured (Issue #1167)
                        <>
                          <p className="font-medium text-gray-700 mb-2" data-testid="no-preferences-title">
                            No booth preferences set{selectedVendorName ? ` for ${selectedVendorName}` : ''}
                          </p>
                          <p className="text-sm text-gray-600 mb-4" data-testid="no-preferences-description">
                            To get personalized booth recommendations, set your booth preferences in your vendor profile.
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                            {selectedVendorId && (
                              <Link
                                to={`/vendors/${selectedVendorId}`}
                                className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 transition font-medium"
                                data-testid="set-preferences-button"
                                onClick={onClose}
                              >
                                Set Booth Preferences
                              </Link>
                            )}
                            <span className="text-gray-400">or</span>
                            <button
                              onClick={() => handleViewModeChange('all')}
                              className="text-secondary hover:text-primary-dark font-medium"
                              data-testid="view-all-booths-button"
                            >
                              View All Booths
                            </button>
                          </div>
                        </>
                      ) : slots.length === 0 && !loading ? (
                        // Scenario 3: Fair Has No Available Booths (Issue #1167)
                        <>
                          <p className="font-medium text-gray-700 mb-2" data-testid="no-availability-title">
                            This fair currently has no available booths
                          </p>
                          <p className="text-sm text-gray-600 mb-4" data-testid="no-availability-description">
                            Check back later or contact the fair manager for more information.
                          </p>
                          <button
                            onClick={onClose}
                            className="text-secondary hover:text-primary-dark font-medium"
                            data-testid="close-modal-button"
                          >
                            Close
                          </button>
                        </>
                      ) : (
                        // Scenario 2: No Matches Found (But Preferences Exist) (Issue #1167)
                        <>
                          <p className="font-medium text-gray-700 mb-2" data-testid="no-matches-title">
                            No booths match your preferences{selectedVendorName ? ` for ${selectedVendorName}` : ''}
                          </p>
                          <p className="text-sm text-gray-600 mb-4" data-testid="no-matches-description">
                            Try viewing all available booths or adjusting your preferences.
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                            <button
                              onClick={() => handleViewModeChange('all')}
                              className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark transition font-medium"
                              data-testid="view-all-booths-button"
                            >
                              View All Booths
                            </button>
                            {selectedVendorId && (
                              <>
                                <span className="text-gray-400">or</span>
                                <Link
                                  to={`/vendors/${selectedVendorId}`}
                                  className="text-purple-600 hover:text-purple-700 font-medium"
                                  data-testid="adjust-preferences-button"
                                  onClick={onClose}
                                >
                                  Adjust Preferences
                                </Link>
                              </>
                            )}
                          </div>
                        </>
                      )
                    ) : (
                      <>
                        <p className="mb-3" data-testid="threshold-filter-empty-message">No booths match your filter criteria ({matchScoreThreshold}%+ match score).</p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                          <button
                            onClick={() => handleMatchScoreThresholdChange(0)}
                            className="text-purple-600 hover:text-purple-700 font-medium"
                            data-testid="show-all-recommendations-button"
                          >
                            Show All Recommendations
                          </button>
                          <span className="text-gray-400">or</span>
                          <button
                            onClick={() => handleViewModeChange('all')}
                            className="text-secondary hover:text-primary-dark font-medium"
                            data-testid="view-all-booths-from-filter-button"
                          >
                            View All Booths
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  filteredRecommendedBooths.map((match, index) => (
                    <div key={match.slot.id} className="border rounded-lg p-4 hover:border-dusty-teal transition">
                      <div className="flex justify-between items-start flex-col md:flex-row gap-3">
                        <div className="flex-grow">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-lg">Booth {match.slot.slot_number}</h4>
                            {index === 0 && (
                              <span className="px-2 py-0.5 bg-moonbone text-ink text-xs font-medium rounded-full">
                                Top Match
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 space-y-1 mt-2">
                            <div>📏 {match.slot.dimensions || 'Size not specified'}</div>
                            {match.slot.has_power && <div>⚡ Power Available</div>}
                            <div className="mt-1">
                              <span className="inline-block bg-gray-100 px-2 py-1 rounded text-xs capitalize">
                                {match.slot.slot_type} Booth
                              </span>
                            </div>
                          </div>
                          {/* Match Reasons */}
                          {match.match_reasons.length > 0 && (
                            <div className="mt-3 space-y-1">
                              {match.match_reasons.slice(0, 3).map((reason, idx) => (
                                <div key={idx} className="flex items-start gap-2 text-xs text-gray-700">
                                  <span className="text-green-600 mt-0.5">✓</span>
                                  <span>{reason}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="text-right w-full md:w-auto flex flex-col items-end gap-2">
                          <BoothMatchIndicator score={match.match_score} size="medium" />
                          <div className="text-2xl font-bold text-primary-dark">
                            {formatCurrency(match.slot.price_override || match.slot.base_price)} {/* Issue #905: Decimal.js formatting */}
                          </div>
                          <div className="text-sm text-gray-500">per day</div>
                          <button
                            onClick={() => handleReserveSlot(match.slot.id)}
                            className="mt-2 bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark transition w-full md:w-auto"
                          >
                            Reserve This Booth
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )
              ) : (
                /* All Booths View */
                loading && page === 1 ? (
                  <div className="text-center py-8 text-gray-500">
                    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
                    Loading available booths...
                  </div>
                ) : error ? (
                  <div className="text-center py-8">
                    <p className="text-red-600 mb-3">{error}</p>
                    <button
                      onClick={() => {
                        setError(null);
                        setPage(1);
                        // fetchSlots will run automatically via useEffect
                      }}
                      className="text-secondary hover:text-primary-dark font-medium"
                    >
                      Retry
                    </button>
                  </div>
                ) : slots.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No booths match your filters. Try adjusting your search.
                  </div>
                ) : (
                  slots.map(slot => (
                    <div key={slot.id} className="border rounded-lg p-4 hover:border-primary transition">
                      <div className="flex justify-between items-start flex-col md:flex-row gap-3">
                        <div className="flex-grow">
                          <h4 className="font-semibold text-lg">Booth {slot.slot_number}</h4>
                          <div className="text-sm text-gray-600 space-y-1 mt-2">
                            <div>📏 {slot.dimensions || 'Size not specified'}</div>
                            {slot.has_power && <div>⚡ Power Available</div>}
                            <div className="mt-1">
                              <span className="inline-block bg-gray-100 px-2 py-1 rounded text-xs capitalize">
                                {slot.slot_type} Booth
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right w-full md:w-auto">
                          <div className="text-2xl font-bold text-primary-dark">
                            {formatCurrency(slot.base_price.toString())} {/* Issue #905: Decimal.js formatting */}
                          </div>
                          <div className="text-sm text-gray-500">per day</div>
                          <button
                            onClick={() => handleReserveSlot(slot.id)}
                            className="mt-3 bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark transition w-full md:w-auto"
                          >
                            Reserve This Booth
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )
              )}
            </div>

            {/* Pagination - Only show for 'all' view */}
            {viewMode === 'all' && !loading && !error && slots.length >= pageSize && totalCount > slots.length && (
              <button
                onClick={handleShowMore}
                disabled={loading}
                className="w-full bg-gray-100 text-gray-700 py-2 rounded hover:bg-gray-200 transition disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Show More Booths'}
              </button>
            )}

            {/* Footer Actions */}
            <div className="flex justify-between mt-6 pt-4 border-t flex-col sm:flex-row gap-3">
              <button
                onClick={onClose}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 transition"
              >
                Close
              </button>
              <Link
                to={`/fairs/${fairId}/slots`}
                className="bg-white text-secondary border border-primary px-6 py-2 rounded hover:bg-secondary-light/50 transition text-center"
                onClick={onClose}
              >
                Browse All Slots
              </Link>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
